// src/api/fulfillment.ts
import { apiClient } from './client';
import type { ApiResponse } from '../types';
import type { Shipment, Package } from '../types';

export const fulfillmentAPI = {
  // Get a specific package
  async getPackage(packageId: string): Promise<ApiResponse<Package>> {
    try {
      const response = await apiClient.get(`/fulfillment/packages/${packageId}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch package',
        status: error.response?.status || 500
      };
    }
  },

  // Assign driver to a package
  async assignDriverToPackage(packageId: string, driverId: number): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/assign-driver/`, {
        driver_id: driverId
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to assign driver to package',
        status: error.response?.status || 500
      };
    }
  },

  // Mark package as shipped
  async markPackageShipped(packageId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/mark-shipped/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to mark package as shipped',
        status: error.response?.status || 500
      };
    }
  },

  // Mark package as delivered
  async markPackageDelivered(packageId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/mark-delivered/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to mark package as delivered',
        status: error.response?.status || 500
      };
    }
  },

  // Get packages for a warehouse (staff view)
  async getWarehousePackages(warehouseId: number): Promise<ApiResponse<Package[]>> {
    try {
      const response = await apiClient.get(`/fulfillment/warehouses/${warehouseId}/packages/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch warehouse packages',
        status: error.response?.status || 500
      };
    }
  },

  // Get warehouses for current staff user
  async getDashboardData(): Promise<ApiResponse<any[]>> {
    try {
      const response = await apiClient.get('/fulfillment/shipments/dashboard-data/');
      console.log(response.data)
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch warehouses',
        status: error.response?.status || 500
      };
    }
  },

  // Get shipments for a warehouse
  async getWarehouseShipments(warehouseId?: number): Promise<ApiResponse<Shipment[]>> {
    try {
      const url = warehouseId
        ? `/fulfillment/warehouses/${warehouseId}/shipments/`
        : '/fulfillment/shipments/my/';
      const response = await apiClient.get(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch shipments',
        status: error.response?.status || 500
      };
    }
  },

  // Start packing a shipment
  async startPacking(shipmentId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/shipments/${shipmentId}/start-packing/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to start packing',
        status: error.response?.status || 500
      };
    }
  },

  // Get a specific shipment
  async getShipment(shipmentId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get(`/fulfillment/shipments/${shipmentId}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch shipment',
        status: error.response?.status || 500
      };
    }
  },

  // Create a package for a shipment
  async createPackage(shipmentId: string, data: { items: any[]; weight?: number }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/shipments/${shipmentId}/create_package/`, data);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to create package',
        status: error.response?.status || 500
      };
    }
  },

  // Mark an individual package as packed
  async markPackagePacked(packageId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/mark_packed/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to mark package as packed',
        status: error.response?.status || 500
      };
    }
  },

  // Mark a shipment as packed (verification step)
  async markShipmentPacked(shipmentId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/shipments/${shipmentId}/mark_shipment_packed/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to mark shipment as packed',
        status: error.response?.status || 500
      };
    }
  },
  // Unpack a package (revert to pending)
  async unpackPackage(packageId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/unpack/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to unpack package',
        status: error.response?.status || 500
      };
    }
  },
  // Remove a package (pending status only)
  async removePackage(packageId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/remove/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to remove package',
        status: error.response?.status || 500
      };
    }
  },
  async removeItemFromPackage(packageId: string, data: { shipment_item_id: string; quantity: number; delete_if_empty?: boolean; }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/remove-item/`, data);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to remove item from package',
        status: error.response?.status || 500
      };
    }
  },

  // Assign items to an existing package
  async assignItemsToPackage(packageId: string, data: { items: { shipment_item_id: string; quantity: number }[] }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/fulfillment/packages/${packageId}/assign-items/`, data);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to assign items to package',
        status: error.response?.status || 500
      };
    }
  },
};